import { Badge, Select } from '@chopchop-se/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { BinRow } from '@warehouse/shared';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { warehouseApi, warehouseKeys } from '../api';
import { BinDrawer } from '../components/BinDrawer';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatQty } from '../../../lib/format';
import { listQueryKey } from '../../../lib/list-query';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

/** Every bin in the warehouse, searchable, with the drawer for its contents. */
export function BinsPage(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const { state, setState, setFilter } = useListState({ defaultSort: 'code', defaultDir: 'asc' });
  const [openBin, setOpenBin] = useState<string | null>(null);

  const { data, isFetching, error } = useQuery({
    queryKey: warehouseKeys.bins(listQueryKey(state)),
    queryFn: () => warehouseApi.listBins(state),
    placeholderData: keepPreviousData,
  });
  useLiveQuery(['warehouse', 'stock'], warehouseKeys.bins());

  const { data: areas } = useQuery({
    queryKey: warehouseKeys.areas(),
    queryFn: () => warehouseApi.listAreas(),
  });

  const columns: DataTableColumn<BinRow>[] = [
    {
      id: 'code',
      header: t('warehouseCore:bins.code'),
      sortField: 'code',
      cell: (row) => <span className="font-mono text-sm text-ink">{row.code}</span>,
    },
    {
      id: 'area',
      header: t('warehouseCore:bins.area'),
      sortField: 'areaCode',
      cell: (row) => `${row.areaCode} · ${row.areaName}`,
      hideBelow: 'md',
    },
    {
      id: 'shelf',
      header: t('warehouseCore:bins.shelf'),
      cell: (row) => row.shelfCode ?? '—',
      hideBelow: 'lg',
    },
    {
      id: 'kind',
      header: t('warehouseCore:bins.kind'),
      sortField: 'kind',
      cell: (row) => t(`warehouseCore:bins.kinds.${row.kind}`),
      hideBelow: 'sm',
    },
    {
      id: 'flags',
      header: t('common:common.status'),
      cell: (row) => (
        <span className="flex flex-wrap gap-1">
          {row.isPickLocation ? <Badge tone="brand">{t('warehouseCore:bins.pickLocation')}</Badge> : null}
          {row.isBlocked ? <Badge tone="danger">{t('warehouseCore:bins.blocked')}</Badge> : null}
        </span>
      ),
    },
    {
      id: 'pallets',
      header: t('warehouseCore:bins.pallets'),
      align: 'right',
      cell: (row) => row.palletCount,
      hideBelow: 'lg',
    },
    {
      id: 'qty',
      header: t('warehouseCore:bins.quantity'),
      align: 'right',
      cell: (row) => <span className="font-mono">{formatQty(row.totalQtyBase)}</span>,
    },
  ];

  return (
    <>
      <PageHeader title={t('warehouseCore:bins.title')} subtitle={t('warehouseCore:bins.subtitle')} />

      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={data?.total ?? null}
        loading={isFetching}
        error={error}
        searchPlaceholder={t('warehouseCore:bins.search')}
        emptyTitle={t('warehouseCore:bins.empty')}
        emptyDescription={t('warehouseCore:bins.emptyHelp')}
        onRowClick={(row) => {
          setOpenBin(row.id);
        }}
        filters={
          <>
            <Select
              aria-label={t('warehouseCore:bins.area')}
              className="w-44"
              value={String(state.filters?.areaId ?? '')}
              placeholder={t('warehouseCore:common.selectArea')}
              options={[
                { value: '', label: t('warehouseCore:common.all') },
                ...(areas ?? []).map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` })),
              ]}
              onChange={(event) => {
                setFilter('areaId', event.target.value || undefined);
              }}
            />
            <Select
              aria-label={t('warehouseCore:bins.kind')}
              className="w-40"
              value={String(state.filters?.kind ?? '')}
              options={[
                { value: '', label: t('warehouseCore:common.all') },
                { value: 'bin', label: t('warehouseCore:bins.kinds.bin') },
                { value: 'floor', label: t('warehouseCore:bins.kinds.floor') },
                { value: 'pillar', label: t('warehouseCore:bins.kinds.pillar') },
              ]}
              onChange={(event) => {
                setFilter('kind', event.target.value || undefined);
              }}
            />
            <Select
              aria-label={t('warehouseCore:bins.blocked')}
              className="w-40"
              value={state.filters?.isBlocked === true ? 'blocked' : ''}
              options={[
                { value: '', label: t('warehouseCore:common.all') },
                { value: 'blocked', label: t('warehouseCore:bins.blocked') },
              ]}
              onChange={(event) => {
                setFilter('isBlocked', event.target.value === 'blocked' ? true : undefined);
              }}
            />
          </>
        }
      />

      <BinDrawer
        binId={openBin}
        onClose={() => {
          setOpenBin(null);
        }}
      />
    </>
  );
}
